import React from 'react';
import { Link } from 'react-router-dom';
import Header from "../layout/Header";
import Footer from '../layout/Footer';
import Toolbar from '../navigation/Toobar';
import Chat from '../components/common/Chat';
import useSEO from '../hooks/useSEO';
import { buildBreadcrumbJsonLd, buildFaqJsonLd, getSiteBrand } from '../utils/seo';

const FAQ_BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'FAQ', path: '/faq' },
];

/** Visible Q&A entries – the same list is published as FAQPage structured data. */
const FAQ_ITEMS = [
  {
    question: 'How do I create an account?',
    answer:
      'Open the Sign Up page, enter your mobile number and a password, then confirm your details. If a verification step is shown, complete it before you log in for the first time.',
  },
  {
    question: 'I forgot my password. What should I do?',
    answer:
      'Use the reset password option on the login screen if it is available for your account, otherwise contact customer support through live chat or the Contact Us page and verify your account details.',
  },
  {
    question: 'Where can I see my balance?',
    answer:
      'After logging in, your balance is shown in the account area at the top of the home page and on your profile. Use the refresh icon to pull the latest balance.',
  },
  {
    question: 'How do I deposit money?',
    answer:
      'Open the Deposit page, select a payment method such as bKash, Nagad, Rocket, Upay, OK Wallet, SureCash, Tap, bank transfer or cryptocurrency, enter the amount in BDT and confirm the payment. If a transaction ID or receipt is requested, submit it so the deposit can be credited.',
  },
  {
    question: 'How long does a deposit take to show up?',
    answer:
      'Most deposits are credited within minutes of confirmation. During peak hours, bank cut-off times or provider outages a deposit can take longer, and any deposit that needs a manual check is credited after the payment is verified.',
  },
  {
    question: 'What is the minimum deposit and withdrawal amount?',
    answer:
      'The minimum and maximum amounts depend on the payment method you choose. The limits for each method are displayed on the Deposit and Withdrawal pages before you confirm the transaction.',
  },
  {
    question: 'How do withdrawals work?',
    answer:
      'Go to the Withdrawal page, choose a payout method, enter the amount and submit your request. Payouts are normally sent back to the same channel used for your deposits, and for safety a withdrawal may require account verification first.',
  },
  {
    question: 'Why is my deposit or withdrawal pending?',
    answer:
      'Processing time depends on the payment provider, network conditions and verification checks. If a request stays pending longer than expected, contact support with the transaction ID, the amount and a screenshot so we can trace it quickly.',
  },
  {
    question: 'How do promotions and bonuses work?',
    answer:
      'Every promotion has its own rules, including who can claim it, the validity period and the turnover requirement. Read the promotion details on the Promotions page before you apply, and make sure your deposit matches the offer conditions.',
  },
  {
    question: 'What is turnover or rollover?',
    answer:
      'Turnover is the total amount you must wager before bonus funds and related winnings can be withdrawn. Your progress towards the requirement is shown in the account area, and the exact multiplier is listed in the rules of each promotion.',
  },
  {
    question: 'Do you offer rebates or cashback?',
    answer:
      'Yes. Regular players can receive weekly or monthly rebates calculated from betting turnover. Rebate campaigns, percentages and payout times are announced on the Promotions page and credited to the account wallet.',
  },
  {
    question: 'How does the referral programme work?',
    answer:
      'Share your personal referral link with friends. When they register through your link and start playing, you earn a commission on their activity according to the current referral terms shown in your account.',
  },
  {
    question: 'Can I use my account on more than one device?',
    answer:
      'You can log in on a phone, tablet or computer, but only one account is allowed per player. Sharing an account or opening duplicates may lead to restrictions, so keep your login details private.',
  },
  {
    question: 'Is my personal information safe?',
    answer:
      'Connections to the platform are encrypted, and account verification and monitoring are used to protect users and reduce fraud. Never share your password, OTP or payment PIN with anyone — support will never ask for them.',
  },
  {
    question: 'Can I take a break or close my account?',
    answer:
      'Playing is for adults only (18+). If you want to take a break from gambling, contact support and we will help with the account control options available on the platform.',
  },
  {
    question: 'How can I contact support?',
    answer:
      'Use the Contact Us page, the in-app live chat or the official social links in the footer. Support is available around the clock and can help with account access, deposits, withdrawals, bonuses and general troubleshooting.',
  },
];

function FAQ({ globalData }) {
  const brandName = globalData?.general?.site_title || getSiteBrand();

  useSEO({
    title: 'FAQ – Deposits, Withdrawals, Bonuses & Account Help',
    description: `Answers to the most common ${brandName} questions: how to register, deposit with bKash, Nagad or Rocket, withdraw winnings, claim bonuses, reset a password and reach support.`,
    keywords: `betting site help, how to deposit with bKash, Nagad deposit, Rocket withdrawal, bonus turnover, betting account verification, ${brandName} support`,
    path: '/faq',
    brand: brandName,
    jsonLd: [buildFaqJsonLd(FAQ_ITEMS), buildBreadcrumbJsonLd(FAQ_BREADCRUMBS)],
  });

  return (
    <div className="v-application--wrap">
      <div>
        <div className="row no-gutters">
          <Header />
        </div>

        <div className="body">
          <div className="page-container v-card v-sheet theme--dark">
            <div className="row">
              <div className="col col-12">
                <div className="pa-4">
                  <nav className="seo-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/">Home</Link> <span aria-hidden="true">›</span>{' '}
                    <span aria-current="page">FAQ</span>
                  </nav>

                  <h1 className="seo-h1">Frequently asked questions about {brandName}</h1>
                  <p className="seo-text">
                    Everything you need to know about creating an account, funding it in BDT with
                    the payment methods used in Bangladesh, withdrawing winnings, claiming bonuses
                    and getting help when something does not go to plan.
                  </p>

                  <h2 className="seo-h2">Account, login and security</h2>
                  {[FAQ_ITEMS[0], FAQ_ITEMS[1], FAQ_ITEMS[2], FAQ_ITEMS[13]].map((item) => (
                    <p className="seo-faq-item" key={item.question}>
                      <strong>Q: {item.question}</strong>
                      <br />
                      A: {item.answer}
                    </p>
                  ))}

                  <h2 className="seo-h2">Deposits in BDT: bKash, Nagad, Rocket and more</h2>
                  {[FAQ_ITEMS[3], FAQ_ITEMS[4], FAQ_ITEMS[5]].map((item) => (
                    <p className="seo-faq-item" key={item.question}>
                      <strong>Q: {item.question}</strong>
                      <br />
                      A: {item.answer}
                    </p>
                  ))}

                  <h2 className="seo-h2">Withdrawals, verification and pending requests</h2>
                  {[FAQ_ITEMS[6], FAQ_ITEMS[7]].map((item) => (
                    <p className="seo-faq-item" key={item.question}>
                      <strong>Q: {item.question}</strong>
                      <br />
                      A: {item.answer}
                    </p>
                  ))}

                  <h2 className="seo-h2">Bonuses, turnover, rebates and referrals</h2>
                  {[FAQ_ITEMS[8], FAQ_ITEMS[9], FAQ_ITEMS[10], FAQ_ITEMS[11]].map((item) => (
                    <p className="seo-faq-item" key={item.question}>
                      <strong>Q: {item.question}</strong>
                      <br />
                      A: {item.answer}
                    </p>
                  ))}

                  <h2 className="seo-h2">Playing responsibly and getting help</h2>
                  {[FAQ_ITEMS[12], FAQ_ITEMS[14], FAQ_ITEMS[15]].map((item) => (
                    <p className="seo-faq-item" key={item.question}>
                      <strong>Q: {item.question}</strong>
                      <br />
                      A: {item.answer}
                    </p>
                  ))}

                  <h2 className="seo-h2">Where to go next</h2>
                  <ul className="seo-links">
                    <li>
                      <Link to="/signup">Create an account</Link>
                    </li>
                    <li>
                      <Link to="/promotion">Current promotions and bonuses</Link>
                    </li>
                    <li>
                      <Link to="/games">Browse sports, casino tables and slots</Link>
                    </li>
                    <li>
                      <Link to="/contact-us">Contact customer support</Link>
                    </li>
                    <li>
                      <Link to="/terms-and-conditions">Terms and conditions</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <Footer sociallink={globalData?.socialLinks || {}} />
          </div>

          <Toolbar />
          <Chat />
        </div>
      </div>
    </div>
  );
}

export default FAQ;
